define((require) => {
    "use strict";

    const $      = require("jquery");
    const Class  = require("shared/util/Class");
    const Ladda  = require("ladda");  // for spinner
    const {Logger} = require("shared/util/Logger");

    const log = new Logger("SequentialButton");

    /**
     * Value to return by sequential buttons that wish to break out of the functionality early, e.g. on
     * input validation error.
     */
    const REJECT = {
        toString() { return "break"; }
    };

    /**
     * A <i>sequential button</i> can be added to form buttons to ensure that the button(s) can only be
     * activated/clicked sequentially, preventing the user from submitting multiple concurrent requests. <p>
     *
     * Optionally, it supports a spinner to be displayed while the button(s) have a pending action. <p>
     *
     * The button action can also be invoked explicitly, e.g. from a form submit via enter, by simply
     * invoking the function returned from the <tt>add(..)</tt> function.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     * @author Antonio Lagrotteria / <a href="http://www.zmags.com">Zmags</a>
     */
    return Class.extend({}, {

        /**
         * Returns a function that when invoked will disable <tt>button</tt> while <tt>action</tt> is pending, making it
         * active again regardless of action success or failure. If <tt>spinner</tt> is false, the returned function
         * will also be added as a click handler on the button. <p>
         *
         * When passing a jQuery object containing more than one DOM element, the spinner behaviour will be added to all
         * the DOM elements for that jQuery object, but only showing the active one.
         *
         * @param {jQuery}              button            The jQuery element(s) for the button; cannot be null.
         * @param {Function|$.Deferred} action            The action to invoke when the button is activated (clicked), either as
         *                                                a scoped function always returning a $.Deferred object for chaining, or
         *                                                a $.Deferred as-is; cannot be null.
         * @param {Boolean}            [spinner]          True to add a spinner to the the button while activated, false otherwise.
         *                                                Defaults to true if not supplied. If no spinner is used, the returned
         *                                                function is added as a click handler on <tt>button</tt>.
         *                                                Defaults to true if not supplied. If no spinner is used, the returned
         *                                                function is added as a click handler on <tt>button</tt>.
         * @param {Boolean}            [waitUntilStopped] True if a spinner is used and the returned promise should not
         *                                                resolve until the spinner is known to have stopped, false
         *                                                otherwise (default). If falsey, the action will be still be
         *                                                performed as soon as the action resolves, but the returned
         *                                                promise will not resolve until the spinner stops.
         * @param {Number}             [delay]            The delay before the spinner should actually stop; defaults
         *                                                to the spinner default. If <tt>waitUntilStopped</tt>
         *                                                is true, the returned promise will not resolve until at least
         *                                                this time has passed.
         *
         * @return {Function} A function that on invocation will disable <tt>button</tt>, call <tt>action</tt>, and
         *                    enable <tt>button</tt> again, regardless if failure or success. The function will return
         *                    a promise that can be used for chaining handlers to <tt>action</tt>.
         */
        add(button, action, spinner, waitUntilStopped, delay) {
            let progress = typeof spinner === "undefined" || spinner;
            let i;
            let active = null;  // $.Deferred
            let disabled = false;
            let stopped = false;

            waitUntilStopped = waitUntilStopped && spinner;
            // Default Ladda delay on stop is 1000 ms, but that is to include the expand/collapse animation; 300 seems
            // to be a good visual compromise...
            delay = delay || 300;

            function handler() {
                // Invariant: we will never get an active button here if invoked by a dom handler, since once the
                //            buttons are disabled once activated/clicked, but the context can still call this
                //            function explicitly, e.g. based on a form submit via enter in some input field...

                if (active) {
                    log.warn("Button is already active: ", button);
                    return active; // deferred!

                // Semi-permanently disabled?
                } else if (disabled) {
                    log.warn("Button is disabled: ", button);
                    return new $.Deferred().reject();
                }

                let i;
                let match = false;

                for (i = 0; i < button.length; i++) {
                    if ($(button.get(i)).is(":visible")) {
                        match = true;
                        break;
                    }
                }

                // Start: the spinner will already disable the button if used...
                if (progress && match) {
                    progress[i].start();
                } else {
                    button.attr("disabled", true);
                }

                stopped = false;

                // Stop: on "always()" callbacks, of if non-Deferred result from "action", or
                // if "action" throws an exception...
                function stop(force, result, error) {
                    function callback() {
                        if (typeof error === "undefined") {
                            error = result === REJECT || result instanceof Error;
                        }
                        if (error) {
                            active.reject(result);
                        } else {
                            active.resolve(result);
                        }
                        active = null;
                        // Make sure disable now if disabled while running...
                        if (disabled) {
                            handler.enable(false);
                        }
                    }

                    if (!stopped && (active || force)) {
                        stopped = true;
                        if (progress && match) {
                            // Do not wait to stop on REJECT...
                            const wait = active && waitUntilStopped && result !== REJECT;
                            progress[i].stop(delay, wait ? callback : null);
                            if (wait) {
                                return;
                            }
                        } else {
                            button.attr("disabled", false);
                        }
                        if (active) { // if we wait, we will already have returned by now...
                            callback();
                        }
                        // Blur button to ensure we get proper styling again...
                        button.blur();
                    }
                }

                // Returns the $.Deferred for possible further chaining. Note that this is not the deferred from
                // the action!
                const promise = new $.Deferred();
                active = promise;  // separate reference to avoid ever returned undefined after "stop"...
                try {
                    const result = (typeof action === "function" ? action() : action);

                    // Result is a deferred. Once it resolves or is rejected, it will trigger the corresponding
                    // methods in "active"...
                    if (result && typeof result.always === "function") {
                        result
                            .done((res) => stop(false, res, false))  // not an error
                            .fail((res) => stop(false, res, true));  // error

                    // If the result is not a deferred, we have to call stop() explicitly, forcing
                    // a stop even though we have no active call anymore...
                    } else {
                        stop(true, result);
                    }

                // In case "action" raises an exception...
                } catch (e) {
                    log.error("Button action raised exception: ", e);
                    // Force stop and reject...
                    stop(true, e, true);
                }
                return promise;  // never null or undefined here, even if stopped...
            }

            // If a spinner is used, it will handle all button click functionality...
            if (progress) {
                progress = [];
                for (i = 0; i < button.length; i++) {
                    progress.push(Ladda.create(button.get(i)));
                }

            // Explicitly listen for button clicks then...
            } else {
                button.on("click", () => { handler(); });
            }

            /**
             * Enables or disables this button. <p>
             *
             * While disabled, it cannot be clicked at all and the <tt>disabled</tt> css class is added to each
             * target button. <p>
             *
             * If this button is currently active, it will be disabled once stopped.
             *
             * @param {Boolean} enable True to enable this button (enabled by default), false to disable it. When
             *                         disabled, the button can never be clicked until re-enabled with this
             *                         function and a true argument.
             */
            handler.enable = (enable) => {
                disabled = !enable;
                if (disabled) {
                    // Running? If so, wait until completion to disable, i.e. from "stop"...
                    if (!active) {
                        button.addClass("disabled");
                        button.attr("disabled", true);
                    }
                } else {
                    button.removeClass("disabled");
                    button.attr("disabled", false);
                }
            };

            return handler;
        },

        /**
         * @type {*}
         * A constant to return by the action/logic executed by a sequential button when the normal (deferred)
         * execution should be rejected, for example because of input validation error on submit. <p>
         *
         * When this value is returned, the overall action of this sequential button will thus resolve in
         * a rejected call, with this constant as the single argument. This way, <b>external</b> <tt>fail</tt> and/or
         * <tt>always</tt> handlers attached to this button can test for this value before deciding what to do.
         */
        REJECT

    });
});



// WEBPACK FOOTER //
// ./src/editor/util/SequentialButton.js